import { Validators } from '@angular/forms';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { Drug } from '@nrwl/rx-models';
import { RxInputComponent } from '@nrwl/rx-input';
import { RxAutocompleteComponent } from '@nrwl/rx-autocomplete';
import { rxRequiredValidator } from '@nrwl/rx-validators';
import {
  RxListCompositeComponent,
  RxReactiveFormCompositeComponent,
  RxTextCompositeComponent,
  RxVideoCompositeComponent,
} from '@nrwl/rx-forms';

import { DialogsConfigService } from '../dialogs-config.service';
import { PhotosValidatorsService } from './photos-validators.service';

@Injectable({
  providedIn: 'root',
})
export class PhotosDialogConfigService extends DialogsConfigService {
  constructor(private photosValidationService: PhotosValidatorsService) {
    super();
  }

  // need async validation
  public newGroup() {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'New Group',
        tip: 'All items must belong to a group.',
        buttons: this.SubmitDismissButtons(),
        fields: [
          {
            placeholder: 'Group Name',
            labelText: null,
            hasInlineInfo: true,
            value: '',
            maxLength: 40,
            validators: [Validators.required, Validators.minLength(2), Validators.maxLength(40)],
            // asyncValidators: [this.formsValidatorsService.categoryUniqValidator(SearchType.AccountLibrary)]
          },
        ],
      },
    };
  }

  public alertClientPhotos(tip) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: tip,
        buttons: this.SubmitButton('OK'),
      },
    };
  }

  public addDescription() {
    return {
      component: RxReactiveFormCompositeComponent,
      bodyClasses: 'description-body',
      headerClasses: 'description-header',
      footerClasses: 'description-footer',
      payload: {
        title: 'Do you want to add a title (Optional)',
        fields: [
          {
            placeholder: '',
            labelText: null,
          },
        ],
        buttons: this.SubmitDismissButtons('OK', 'CANCEL'),
      },
    };
  }

  public removeGroup(categoryName: string) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: `[TODO Message]: Do you want to delete Category "${categoryName}"?`,
        tip: '[TODO Description]: Explanation message',
        buttons: this.SubmitDismissButtons('Delete'),
      },
    };
  }

  public removeItems() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: `[TODO Message]: Are you sure?`,
        tip: "[TODO Description]: You don't have possibility to restore the deleted items",
        buttons: this.SubmitDismissButtons('Delete'),
      },
    };
  }

  public editProduct(category: Drug) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Do you want to rename Product?',
        tip: 'All products are catalogued by title, providers can filter products by these titles.',
        fields: [
          {
            placeholder: 'Name',
            labelText: null,
            hasInlineInfo: true,
            value: category.name,
            maxLength: 50,
            validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)],
            asyncValidators: [this.photosValidationService.checkName(category.name)],
          },
        ],
        buttons: this.SubmitDismissButtons(),
      },
    };
  }

  public createProduct(types, manufacturer, units) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Create Treatment Product',
        fields: [
          {
            placeholder: 'Name',
            labelText: null,
            hasInlineInfo: true,
            value: '',
            maxLength: 50,
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(50)],
            component: RxInputComponent,
            asyncValidators: [this.photosValidationService.checkName(null)],
          },
          {
            component: RxAutocompleteComponent,
            items: units,
            searchable: false,
            placeholder: 'Units',
            validators: [Validators.required],
          },
        ],
        buttons: this.SubmitDismissButtons(),
      },
    };
  }

  public cloneGroup() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: '[TODO Message]: Are you sure you would like to copy this group?',
        tip: '[TODO Description]: A new empty group will be added',
        buttons: this.SubmitDismissButtons('Confirm', 'Cancel'),
      },
    };
  }

  public moveItems(items$: Observable<any[]>) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Move Item(s) to',
        items$: items$,
        buttons: this.SubmitDismissButtons('Move'),
      },
    };
  }

  // TODO think about common service for bottom bar confirmation dialogs
  // remember that there is no multiple extends in TypeScript
  public deselectAllConfirmation() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Deselect Products(s)',
        tip: 'All selected products will be deselected by this action.',
        buttons: this.SubmitDismissButtons('Yes', 'No'),
      },
    };
  }

  public confirmResetChanges() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Confirm changes',
        tip: 'Unsaved changes may be lost',
        buttons: this.SubmitDismissButtons('Proceed', 'Cancel'),
      },
    };
  }

  public confirmMove(category: any, items: Drug[]) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: '[TODO Message:] Move Item(s)',
        tip: `[TODO Description]: Move ${items.length} item(s) to '${category.name}'?`,
        buttons: this.SubmitDismissButtons('Yes'),
      },
    };
  }

  public confirmClone(count?: number) {
    let title = 'Do you want to clone Product?';

    if (count && count > 1) {
      title = `Clone ${count} Product(s)?`;
    }

    return {
      component: RxTextCompositeComponent,
      payload: {
        title,
        tip: 'All cloned products will get new Title and source content.',
        buttons: this.SubmitDismissButtons('Clone'),
      },
    };
  }

  public confirmArchive() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Do you want to archive selected Product?',
        tip: 'The archived product will be stored in "Archived" tab',
        buttons: this.SubmitDismissButtons('Archive'),
      },
    };
  }

  public confirmRestore() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Do you want to restore selected Product?',
        tip: 'The restored product will be available in "Custom" tab.',
        buttons: this.SubmitDismissButtons('Restore'),
      },
    };
  }

  public confirmTabChange() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'Do you want to deny selection?',
        buttons: this.SubmitDismissButtons('Proceed'),
      },
    };
  }

  public confirmLeavingPage() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Confirm Navigation',
        tip: 'You have unsaved changes. Are you sure you want to leave this page?',
        buttons: this.SubmitDismissButtons('Leave this Page', 'Stay on this Page'),
      },
    };
  }

  public helpCenter() {
    return {
      component: RxVideoCompositeComponent,
      payload: {
        title: 'Help Center',
        buttons: this.DismissButton(),
      },
    };
  }
}
